import { forwardRef } from "react";
import type { InputHTMLAttributes } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

// 16px (text-base) is a floor, not a stylistic choice: anything smaller
// makes iOS Safari zoom the viewport on focus. See DESIGN.md §11.
const BASE_CLASSES =
  "w-full rounded-none border bg-[var(--bg-input)] px-3 py-2.5 font-body text-base text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none transition-[border-color,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-50";

const STATE_CLASSES = {
  normal: "border-[var(--border)] focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]",
  error: "border-danger focus:border-danger focus:shadow-[0_0_0_3px_var(--danger-soft)]",
};

/**
 * Carbon-style text input: flat, hairline border, orange focus ring,
 * 16px minimum font (iOS zoom-on-focus fix baked in by default).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error = false, className = "", ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={error || undefined}
      className={`${BASE_CLASSES} ${error ? STATE_CLASSES.error : STATE_CLASSES.normal} ${className}`}
      {...props}
    />
  ),
);

Input.displayName = "Input";

export default Input;
